import { routingFetch } from "../helpers";

export interface RestaurantAsset {
  id: string;
  /** Null for a group-owned asset (no single restaurant). */
  restaurant_id: number | null;
  business_unit_id: number | null;
  type: string;
  directory: string;
  meta: Record<string, unknown>;
  created_at: string;
  url: string;
}

async function uploadError(
  response: Response,
  fallback: string,
): Promise<Error> {
  try {
    const payload = (await response.json()) as { error?: unknown };
    if (typeof payload.error === "string" && payload.error.trim()) {
      return new Error(payload.error);
    }
  } catch {
    // The server or edge may return a non-JSON error page.
  }
  return new Error(`${fallback} (HTTP ${response.status})`);
}

/**
 * Asset API bound to one owner surface. The Shop and Group asset managers share
 * the presentation component and differ only in this base path; ownership is
 * resolved server-side from the authenticated session, so the client never sends
 * an owner id. Requests go through `routingFetch`, which keeps only the path
 * and query and sends them to the current API server.
 */
export function createUploadsAPI(basePath: string) {
  const base = () => `${window.location.origin}${basePath}`;

  return {
    getAssets: async (params?: {
      type?: string;
      directory?: string;
    }): Promise<RestaurantAsset[]> => {
      const url = new URL(base());
      if (params?.type) url.searchParams.set("type", params.type);
      if (params?.directory)
        url.searchParams.set("directory", params.directory);
      const resp = await routingFetch(url.toString(), { credentials: "include" });
      if (resp.ok) return (await resp.json()) as RestaurantAsset[];
      throw new Error("Failed to fetch assets");
    },

    uploadAsset: async (
      file: File | Blob,
      type: string,
      metadata?: Record<string, unknown>,
    ): Promise<{ key: string; assetId: string; url: string }> => {
      const url = new URL(base());
      url.searchParams.set("type", type);
      if (metadata && Object.keys(metadata).length > 0) {
        url.searchParams.set("metadata", JSON.stringify(metadata));
      }
      const resp = await routingFetch(url.toString(), {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
        credentials: "include",
      });
      if (resp.ok) {
        return (await resp.json()) as {
          key: string;
          assetId: string;
          url: string;
        };
      }
      throw await uploadError(resp, "Failed to upload asset");
    },

    /** Merge a meta patch and/or move the asset to another collection. */
    updateAsset: async (
      assetId: string,
      patch: { meta?: Record<string, unknown>; type?: string },
    ): Promise<RestaurantAsset> => {
      const resp = await routingFetch(`${base()}/${assetId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
        credentials: "include",
      });
      if (resp.ok) return (await resp.json()) as RestaurantAsset;
      throw new Error("Failed to update asset");
    },

    /** Persist a manual ordering of one collection (writes meta.position). */
    reorderAssets: async (
      type: string,
      orderedIds: string[],
    ): Promise<{ success: boolean }> => {
      const resp = await routingFetch(`${base()}/reorder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, orderedIds }),
        credentials: "include",
      });
      if (resp.ok) return (await resp.json()) as { success: boolean };
      throw new Error("Failed to reorder assets");
    },

    /** Replace the bytes of an existing asset (reupload); the URL changes. */
    replaceAsset: async (
      assetId: string,
      file: File | Blob,
    ): Promise<{ key: string; assetId: string; url: string }> => {
      const resp = await routingFetch(`${base()}/${assetId}`, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
        credentials: "include",
      });
      if (resp.ok) {
        return (await resp.json()) as {
          key: string;
          assetId: string;
          url: string;
        };
      }
      throw await uploadError(resp, "Failed to replace asset");
    },

    deleteAsset: async (assetId: string): Promise<{ success: boolean }> => {
      const resp = await routingFetch(`${base()}/${assetId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (resp.ok) return (await resp.json()) as { success: boolean };
      // Cleanup can be retried after a partially completed batch.
      if (resp.status === 404) return { success: true };
      throw new Error("Failed to delete asset");
    },
  };
}

export type UploadsAPI = ReturnType<typeof createUploadsAPI>;

/** Shop-scoped assets (default). */
export const uploadsAPI = createUploadsAPI("/api/v2/uploads");
/** Group-scoped assets (group admin). */
export const groupUploadsAPI = createUploadsAPI("/api/group/uploads");
